import { logger } from '@adornis/base/logging.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { AdornisUser } from '@adornis/users/db/a-user.js';
import { projectConfig } from '@chancenwerk/config/config.js';
import { DateTime } from 'luxon';
import { existsSync } from 'node:fs';
import { dirname, join } from 'node:path';
import { fileURLToPath } from 'node:url';
import { Worker } from 'node:worker_threads';
import { ChancenwerkUser } from '../master-data-management/users/entities/ChancenwerkUser.js';
import { ExternalContactPerson } from '../master-data-management/users/entities/ExternalContactPerson.js';
import { ExternerMitarbeiter } from '../master-data-management/users/entities/ExternerMitarbeiter.js';
import { Mitarbeiter } from '../master-data-management/users/entities/Mitarbeiter.js';
import { Student } from '../master-data-management/users/entities/Student.js';
import { DuplicateDataTracker } from './DuplicateDataTracker.js';
import { searchAllAccounts } from './searchForDublicatesUtil.js';

export async function searchForDuplicates(similarityThreshold: number = 0.5) {
  const collection = await getRawCollection(DuplicateDataTracker._collectionName);
  await collection.deleteMany({ blacklisted: false });
  await searchAllAccounts(similarityThreshold);
}

const WORKER_FILE = '../node-workers/searchForDuplicate.worker.mjs';

/**
 * The worker bundle is built per entrypoint into `<entrypoint>/dist/node-workers/`.
 *
 * This module however ships as an external package (`pnpm deploy` puts it under
 * `node_modules/.pnpm/@chancenwerk+shared@…/`), so `import.meta.url` points into the
 * package - not into the entrypoint's `dist/`. Resolving relative to `process.argv[1]`
 * (the running `dist/server/index.mjs`) is what the data-loader worker already does.
 * The `import.meta.url` candidate is kept as a fallback for setups that bundle this
 * module into the server output.
 */
function resolveWorkerPath() {
  const candidates = [
    process.argv[1] ? join(dirname(process.argv[1]), WORKER_FILE) : undefined,
    join(dirname(fileURLToPath(import.meta.url)), WORKER_FILE),
  ].filter(Boolean) as string[];

  const resolved = candidates.find(candidate => existsSync(candidate));
  if (!resolved) {
    throw new Error(`Duplikat-Worker nicht gefunden. Gesucht in: ${candidates.join(', ')}`);
  }

  return resolved;
}

export async function searchForDuplicateForUserData(personData: ChancenwerkUser, similarityThreshold: number = 0.45) {
  const selector = {
    $and: [
      {
        _class: {
          $in: [
            'VerwaltungsMitarbeiter', // * manual because of cyclic dependency
            Mitarbeiter._class,
            ExternerMitarbeiter._class,
            ExternalContactPerson._class,
            Student._class,
          ],
        },
      },
      {
        _id: { $ne: personData._id },
      },
      {
        $or: [
          { 'kuendigung.austrittsdatum': { $exists: false } },
          { 'kuendigung.austrittsdatum': null },
          { 'kuendigung.austrittsdatum': undefined },
          { 'kuendigung.austrittsdatum': { $gt: DateTime.now().toJSDate() } },
        ],
      },
    ],
  };

  const userCollection = await getRawCollection<ChancenwerkUser>(AdornisUser._collectionName);
  const numberOfDocuments = await userCollection.countDocuments(selector);
  const numberOfThreads = Math.min(
    Math.ceil(numberOfDocuments / 500),
    parseInt(projectConfig.get('DUPLICATE_SEARCH_MAX_THREADS')),
  );

  const start = DateTime.now();

  logger.info(
    start.toFormat('dd.LL.yyyy HH:mm:ss:SSS') +
      ' Started multithreaded searching for duplicates. Number Of Workers: ' +
      numberOfThreads,
  );

  // Process workers with limited concurrency to avoid memory spikes
  const MAX_CONCURRENT_WORKERS = 2;
  const result: string[] = [];
  const workerPath = resolveWorkerPath();
  const personDataJson = personData.toJSON(); // Serialize once, not per worker

  for (let batchStart = 0; batchStart < numberOfThreads; batchStart += MAX_CONCURRENT_WORKERS) {
    const batchEnd = Math.min(batchStart + MAX_CONCURRENT_WORKERS, numberOfThreads);
    const batchResults = await Promise.all(
      [...new Array(batchEnd - batchStart)].map((_, i) => {
        const index = batchStart + i;
        return new Promise<string>((resolve, reject) => {
          logger.info('Started worker: DublicateSearchWorker ' + index);
          const worker = new Worker(workerPath, {
            workerData: {
              skip: 500 * index,
              limit: 500,
              user: personDataJson,
              similarityThreshold,
            },
            name: 'DublicateSearchWorker ' + (index + 1),
          });
          worker.on('message', resolve);
          worker.on('error', reject);
          worker.on('exit', code => {
            if (code !== 0) reject(new Error(`Worker stopped with exit code ${code}`));
          });
        });
      }),
    );
    result.push(...batchResults);
  }

  const end = DateTime.now();
  logger.info(
    end.toFormat('dd.LL.yyyy HH:mm:ss:SSS') +
      ' Finished multithreaded searching for duplicates. Took: ' +
      (end.toMillis() - start.toMillis()) / 1000 +
      's',
  );

  const possibleDuplicates: DuplicateDataTracker[] = result.map(jsonString => JSON.parse(jsonString)).flat();
  return possibleDuplicates;
}
